// One content unit (track, question, explainer, module, safety screen): its
// id, inline 30-day stats with a weak-unit flag, check markers, the "Suggest
// rewrite" copilot and the unit's comment thread.

import { useState } from "react";
import { errorText, suggestRewrite } from "../../services/talkTracks";
import type { UnitSuggestions } from "../../types/talkTrack";
import { CommentThread } from "./Comments";
import { isWeakUnit, unitDomId, unitStatsLine } from "./format";
import { useUnitContext } from "./unitContext";

type Panel = "none" | "suggest" | "comments";

export function UnitCard({
  unitId,
  kind,
  title,
  onApplySuggestion,
  onRemove,
  children,
}: {
  unitId: string;
  kind: string;
  title: string;
  // Units the copilot can rewrite (questions, explainers, modules). Receives
  // the chosen wording; only offered on drafts.
  onApplySuggestion?: (text: string) => void;
  onRemove?: () => void;
  children: React.ReactNode;
}) {
  const ctx = useUnitContext();
  const stats = ctx.statsByUnit.get(unitId);
  const weak = isWeakUnit(stats);
  const checks = ctx.checksByUnit.get(unitId) || [];
  const errors = checks.filter((c) => c.level === "error");
  const warnings = checks.filter((c) => c.level === "warning");
  const comments = ctx.commentsByUnit.get(unitId) || [];
  const openComments = comments.filter((c) => !c.resolved).length;

  const [panel, setPanel] = useState<Panel>("none");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<UnitSuggestions | null>(null);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);

  async function suggest() {
    setPanel("suggest");
    setSuggesting(true);
    setSuggestError(null);
    setSuggestions(null);
    setApplied(null);
    try {
      setSuggestions(await suggestRewrite(ctx.topicId, ctx.version, unitId));
    } catch (e) {
      setSuggestError(errorText(e));
    } finally {
      setSuggesting(false);
    }
  }

  const toggle = (p: Panel) => setPanel((cur) => (cur === p ? "none" : p));

  return (
    <div
      id={unitDomId(unitId)}
      className={`tt-unit${weak ? " weak" : ""}${ctx.focusedUnit === unitId ? " focused" : ""}`}
    >
      <div className="tt-unit-head">
        <span className="tt-unit-kind">{kind}</span>
        <span className="tt-unit-title">{title || <span className="muted">(untitled)</span>}</span>
        <div className="spacer" />
        {errors.length > 0 && (
          <span className="badge tt-badge-error" title={errors.map((c) => c.message).join("\n")}>
            {errors.length} error{errors.length === 1 ? "" : "s"}
          </span>
        )}
        {warnings.length > 0 && (
          <span className="badge tt-badge-warning" title={warnings.map((c) => c.message).join("\n")}>
            {warnings.length} warning{warnings.length === 1 ? "" : "s"}
          </span>
        )}
        {onRemove && ctx.editable && (
          <button className="btn btn-sm btn-ghost" onClick={onRemove} aria-label={`Remove ${kind.toLowerCase()} ${unitId}`}>
            ✕
          </button>
        )}
      </div>

      <div className="tt-unit-meta">
        <span className="mono">{unitId}</span>
        {stats ? (
          <span className={weak ? "tt-weak-text" : ""}>{unitStatsLine(stats)}</span>
        ) : (
          <span>no moves yet</span>
        )}
        {weak && <span className="badge tt-badge-error">weak</span>}
        <div className="spacer" />
        {onApplySuggestion && (
          <button className="btn btn-sm btn-ghost" onClick={() => (panel === "suggest" ? setPanel("none") : suggest())}
            disabled={suggesting}>
            {suggesting ? "Suggesting…" : "Suggest rewrite"}
          </button>
        )}
        <button className="btn btn-sm btn-ghost" onClick={() => toggle("comments")} aria-expanded={panel === "comments"}>
          Comments{comments.length ? ` (${openComments} open)` : ""}
        </button>
      </div>

      {panel === "suggest" && (
        <div className="tt-suggest">
          {suggesting && <div className="muted tt-small">Asking the copilot for rewrites…</div>}
          {suggestError && <div className="error-text">{suggestError}</div>}
          {suggestions && (
            <>
              {suggestions.rationale && <div className="muted tt-small">{suggestions.rationale}</div>}
              {suggestions.suggestions.map((s) => (
                <div key={s} className="tt-suggestion">
                  <span className="tt-suggestion-text">{s}</span>
                  {ctx.editable && onApplySuggestion ? (
                    <button
                      className="btn btn-sm"
                      disabled={applied === s}
                      onClick={() => {
                        onApplySuggestion(s);
                        setApplied(s);
                      }}
                    >
                      {applied === s ? "Applied" : "Apply"}
                    </button>
                  ) : null}
                </div>
              ))}
              {!ctx.editable && <div className="muted tt-small">Start a draft to apply a suggestion.</div>}
            </>
          )}
        </div>
      )}

      {panel === "comments" && (
        <CommentThread
          topicId={ctx.topicId}
          version={ctx.version}
          unitId={unitId}
          comments={comments}
          onComment={ctx.onComment}
          onError={ctx.onError}
        />
      )}

      {children}
    </div>
  );
}
